import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Globe,
  Smartphone,
  Palette,
  ShieldCheck,
  Search,
  Rocket,
  CheckCircle2,
  ArrowRight,
  LayoutDashboard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CollegeWebsitePage() {
  const highlights = [
    {
      icon: LayoutDashboard,
      title: 'Professional College Branding',
      description:
        "A polished digital presence that reflects your institution's academic reputation and attracts prospective students.",
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Design',
      description:
        'Fully responsive pages so your college website looks excellent on phones, tablets, and desktop.',
    },
    {
      icon: Palette,
      title: 'Custom UI And Theme',
      description:
        'Design aligned to your college identity with colors, banners, department pages, and achievement showcases.',
    },
    {
      icon: Search,
      title: 'SEO-Ready Structure',
      description:
        'Optimized page structure and metadata to improve discoverability on Google and local search.',
    },
    {
      icon: ShieldCheck,
      title: 'Secure And Reliable',
      description:
        'Best-practice setup with performance and security essentials to keep your website stable.',
    },
    {
      icon: Rocket,
      title: 'Fast Launch Support',
      description:
        'End-to-end support from requirement gathering to deployment and post-launch improvements.',
    },
  ];

  const deliverables = [
    'Home, About, Admissions, Departments, Placements, and Contact pages',
    'Events, notices, and gallery module for campus updates',
    'Lead and enquiry forms integrated with your communication workflow',
    'Simple content update support for college admins',
  ];

  return (
    <div data-testid="college-website-page" className="pt-20">
      <section className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 mb-6">
                <Globe className="h-4 w-4 text-emerald-light" />
                <span className="text-sm font-medium text-slate-100">
                  College Website Service
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl tracking-tighter font-black mb-6 font-heading">
                Complete Website Solution For Colleges
              </h1>
              <p className="text-base leading-relaxed text-slate-300 mb-8 max-w-2xl">
                Build a high-converting digital presence for your institution with
                a modern, fast, and responsive website designed specifically for
                higher education platforms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button
                    data-testid="college-website-demo-button"
                    size="lg"
                    className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
                  >
                    Book Free Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="college-website-contact-button"
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8"
                  >
                    Talk To Our Team
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative"
            >
              <div className="rounded-2xl border border-white/20 bg-white/10 p-6 shadow-2xl backdrop-blur">
                <div className="rounded-xl bg-white p-6">
                  <div className="space-y-4">
                    <div className="h-3 w-28 rounded-full bg-emerald-light/60" />
                    <div className="h-10 w-full rounded-lg bg-slate-100" />
                    <div className="grid grid-cols-2 gap-3">
                      <div className="h-20 rounded-lg bg-slate-100" />
                      <div className="h-20 rounded-lg bg-slate-100" />
                    </div>
                    <div className="h-16 rounded-lg bg-emerald-light/20 border border-emerald-light/30" />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-5 -left-5 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
                <p className="text-xs text-slate-500">Launch Timeline</p>
                <p className="text-sm font-semibold text-navy">7 - 14 Days*</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              What You Get
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              A complete college website package tailored for admissions, student
              engagement, and institutional credibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {highlights.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4 inline-flex rounded-xl bg-emerald-light/15 p-3">
                  <item.icon className="h-6 w-6 text-emerald" />
                </div>
                <h3 className="text-xl font-semibold text-navy mb-2 font-heading">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-12 shadow-sm">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
              Core Deliverables
            </h2>
            <div className="space-y-4">
              {deliverables.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs text-slate-500">
              *Timelines vary based on number of pages and customization needs.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
